<template>
  <div id="app" class="verify-program-page">
    <!-- Hero Banner -->
    <div class="hero-banner">
      <div class="banner-overlay"></div>
      <div class="banner-content">
        <div class="banner-icon-wrapper">
          <v-icon color="white" size="48">mdi-shield-check</v-icon>
        </div>
        <h1 class="banner-title">Verification Portal</h1>
        <p class="banner-subtitle">
          Verify program details or membership information
        </p>
      </div>
    </div>

    <v-container class="modern-container">
      <v-row justify="center">
        <v-col cols="12" md="11" lg="10">

          <!-- Tabs -->
          <v-card class="tabs-card" elevation="8">
            <v-tabs
              v-model="activeTab"
              class="modern-tabs"
              background-color="transparent"
              color="primary"
              grow
            >
              <v-tab class="modern-tab">
                <v-icon class="mr-2">mdi-calendar-check</v-icon>
                Verify Program
              </v-tab>
              <v-tab class="modern-tab">
                <v-icon class="mr-2">mdi-account-check</v-icon>
                Verify Membership
              </v-tab>
            </v-tabs>
          </v-card>
          <!-- Tab Items -->
          <v-tabs-items v-model="activeTab" class="mt-4">

            <!-- Verify Program Tab -->
            <v-tab-item>

          <!-- Search Card -->
          <v-card class="search-card" elevation="8">
            <div class="search-card-header">
              <v-icon color="white" class="mr-2">mdi-magnify</v-icon>
              <span>Search Program</span>
            </div>

            <v-card-text class="search-card-content">
              <v-alert
                dense
                text
                color="info"
                border="left"
                class="mb-4"
              >
                <div class="d-flex align-center">
                  <v-icon color="info" small class="mr-2">mdi-information</v-icon>
                  <span class="caption">
                    Enter <strong>Coordinator Email</strong> to get Program Code/Details <strong>OR</strong> enter <strong>Program Code</strong> to get Participant Details
                  </span>
                </div>
              </v-alert>

              <v-text-field
                name="ProgramId"
                :rules="nameRules"
                label="Coordinator Email or Program Code"
                outlined
                dense
                class="modern-search-field"
                id="ProgramId"
                v-model="ProgramId"
                type="text"
                placeholder="Enter coordinator email or program code"
                required
                hide-details="auto"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-email-search</v-icon>
                </template>
              </v-text-field><br>

              <div class="search-actions">
                <v-btn
                  class="modern-validate-btn"
                  elevation="2"
                  @click="validateProgramId()"
                >
                  <v-icon small class="mr-2">mdi-check-circle</v-icon>
                  Validate
                </v-btn>
                <v-btn
                  class="modern-clear-btn"
                  elevation="2"
                  outlined
                  @click="clear()"
                >
                  <v-icon small class="mr-2">mdi-refresh</v-icon>
                  Clear
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- No Data Alert -->
          <v-alert
            v-if="noData"
            type="warning"
            class="modern-alert mt-4"
            prominent
            border="left"
            colored-border
            elevation="4"
          >
            <div class="alert-content">
              <div>
                <div class="alert-title">No Program Found</div>
                <div class="alert-message">No program details found for the entered email or code. Please verify and try again.</div>
              </div>
            </div>
          </v-alert>

          <!-- Important Note Card -->
          <!-- <v-card v-if="profileData && profileData.length > 0" class="important-note-card mt-4" elevation="4">
            <div class="note-card-header">
              <v-icon color="white" size="18" class="mr-2">mdi-information</v-icon>
              <span>Important Note</span>
            </div>
            <v-card-text class="note-card-content">
              <ul class="note-list">
                <li class="note-item">
                  <v-icon color="primary" size="16" class="mr-2">mdi-check-circle</v-icon>
                  <span>Participants Membership should be active and paid for 2026.</span>
                </li>
                <li class="note-item">
                  <v-icon color="primary" size="16" class="mr-2">mdi-check-circle</v-icon>
                  <span>Participants with a regular Tamil Sangam membership can participate in <strong>1 program only</strong>.</span>
                </li>
                <li class="note-item">
                  <v-icon color="primary" size="16" class="mr-2">mdi-check-circle</v-icon>
                  <span>Members from <strong>Paarivallal family</strong> can participate in <strong>2 programs</strong> preferably in different categories like Tamil Isai and Drama.</span>
                </li>
                <li class="note-item">
                  <v-icon color="warning" size="16" class="mr-2">mdi-alert</v-icon>
                  <span><strong>Participant violating above any rule will not be allowed to perform in any program.</strong></span>
                </li>
              </ul>
            </v-card-text>
          </v-card> -->

          <!-- Data Table Card -->
          <v-card v-if="profileData && profileData.length > 0" class="data-table-card mt-6" elevation="8">

            <!-- Mobile scroll hint -->
            <div class="mobile-scroll-hint">
              <v-icon small class="mr-1">mdi-gesture-swipe-horizontal</v-icon>
              <span>Swipe to view all columns</span>
            </div>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="profileData"
                item-key="_id"
                class="modern-data-table"
                :loading="loading"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:item.programName="{ item }">
                  <div class="program-name-cell">
                    <v-icon color="primary" size="20" class="mr-2">mdi-calendar-star</v-icon>
                    <span class="program-name">{{ item.programName }}</span>
                  </div>
                </template>

                <template v-slot:item.coordinatorName="{ item }">
                  <div class="coordinator-cell">
                    <v-avatar size="28" color="primary" class="mr-2">
                      <v-icon small color="white">mdi-account</v-icon>
                    </v-avatar>
                    <span>{{ item.coordinatorName }}</span>
                  </div>
                </template>

                <template v-slot:item.coordinatorEmail="{ item }">
                  <div class="email-cell">
                    <v-icon color="success" size="18" class="mr-1">mdi-email</v-icon>
                    <span>{{ item.coordinatorEmail }}</span>
                  </div>
                </template>

                <template v-slot:item.programId="{ item }">
                  <v-chip
                    class="program-code-chip"
                    small
                    label
                  >
                    {{ item.programId }}
                  </v-chip>
                </template>

                <template v-slot:item.status="{ item }">
                  <span>{{ item.status === 'Submitted Successfully' ? 'Submitted' : item.status }}</span>
                </template>

                <template v-slot:item.zoneDetails="{ item }">
                  <v-chip
                    label
                    :color="getZoneColor(item.zoneDetails)"
                    class="zone-chip"
                    dark
                    small
                  >
                    <v-icon small class="mr-1">mdi-map-marker</v-icon>
                    {{ item.zoneDetails }}
                  </v-chip>
                </template>

                <template v-slot:item.timeSlot="{ item }">
                  <v-chip
                    label
                    :color="getColor(item.status)"
                    class="status-chip"
                    dark
                    small
                  >
                    <v-icon small class="mr-1">
                      {{ item.status === 'Submitted Successfully' ? 'mdi-check' : 'mdi-clock-alert' }}
                    </v-icon>
                    {{ item.timeSlot }}
                  </v-chip>
                </template>

                <template v-slot:item.changes="{ item }">
                  <span v-if="item.programMatch">{{ item.programMatch }}</span>
                </template>

                <template v-slot:loading>
                  <div class="loading-wrapper">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="48"
                    ></v-progress-circular>
                    <p class="loading-text">Loading program details...</p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Participant Data Table Card -->
          <v-card v-if="pData && pData.length > 0" class="data-table-card mt-4" elevation="4">
            <div class="table-card-header">
              <v-icon color="white" class="mr-2">mdi-account-group</v-icon>
              <span>Participant Details</span>
            </div>

            <div class="mobile-scroll-hint">
              <v-icon small class="mr-1">mdi-gesture-swipe-horizontal</v-icon>
              <span>Swipe to view all columns</span>
            </div>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="pheaders"
                :items="pData"
                item-key="_id"
                class="modern-data-table"
                :loading="loading"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:item.Participant="{ item }">
                  <div class="program-name-cell">
                    <v-avatar size="28" color="primary" class="mr-2">
                      <v-icon small color="white">mdi-account</v-icon>
                    </v-avatar>
                    <span class="program-name">{{ item.Participant }}</span>
                  </div>
                </template>

                <template v-slot:item.EmailAddress="{ item }">
                  <div class="email-cell">
                    <v-icon color="success" size="18" class="mr-1">mdi-email</v-icon>
                    <span>{{ item.EmailAddress }}</span>
                  </div>
                </template>

                <template v-slot:item.ProgramName="{ item }">
                  <div class="program-name-cell">
                    <v-icon color="primary" size="20" class="mr-2">mdi-calendar-star</v-icon>
                    <span class="program-name">{{ item.ProgramName }}</span>
                  </div>
                </template>

                <template v-slot:item.ProgramCode="{ item }">
                  <v-chip
                    class="program-code-chip"
                    small
                    label
                  >
                    {{ item.ProgramCode }}
                  </v-chip>
                </template>

                <template v-slot:item.Coordinator="{ item }">
                  <div class="coordinator-cell">
                    <v-avatar size="28" color="secondary" class="mr-2">
                      <v-icon small color="white">mdi-account-tie</v-icon>
                    </v-avatar>
                    <span>{{ item.Coordinator }}</span>
                  </div>
                </template>

                <template v-slot:loading>
                  <div class="loading-wrapper">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="48"
                    ></v-progress-circular>
                    <p class="loading-text">Loading participant details...</p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Feedback Card -->
          <!-- <v-card v-if="profileData && profileData.length > 0" class="feedback-card mt-4" elevation="4">
            <div class="feedback-card-header">
              <v-icon color="white" class="mr-2">mdi-comment-check</v-icon>
              <span>Program Verification Feedback</span>
            </div>

            <v-card-text class="feedback-card-content">
              <v-radio-group v-model="feedbackType" class="feedback-options" hide-details>
                <v-radio
                  value="looksGood"
                  color="success"
                  class="feedback-radio"
                >
                  <template v-slot:label>
                    <div class="checkbox-label">
                      <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span>Looks Good</span>
                    </div>
                  </template>
                </v-radio>

                <v-radio
                  value="needsUpdate"
                  color="warning"
                  class="feedback-radio"
                >
                  <template v-slot:label>
                    <div class="checkbox-label">
                      <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
                      <span>Needs Update</span>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>

              <v-expand-transition>
                <div v-if="feedbackType === 'needsUpdate'" class="comment-section mt-4">
                  <v-textarea
                    v-model="feedbackComments"
                    label="Comments"
                    placeholder="Please describe what needs to be updated..."
                    outlined
                    rows="4"
                    counter
                    maxlength="500"
                    class="feedback-textarea"
                    :rules="[v => !!v || 'Comments are required when selecting Needs Update']"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary">mdi-comment-text</v-icon>
                    </template>
                  </v-textarea>
                </div>
              </v-expand-transition>

              <div v-if="feedbackType" class="feedback-actions mt-4">
                <v-btn
                  color="primary"
                  class="submit-feedback-btn"
                  elevation="2"
                  large
                  :disabled="feedbackType === 'needsUpdate' && !feedbackComments"
                  @click="submitFeedback"
                >
                  <v-icon left>mdi-send</v-icon>
                  Submit Feedback
                </v-btn>
              </div>
            </v-card-text>
          </v-card> -->

            </v-tab-item>

            <!-- Verify Membership Tab -->
            <v-tab-item>
              <VIV />
            </v-tab-item>

          </v-tabs-items>

        </v-col>
      </v-row>
    </v-container>

    <!-- Success Dialog -->
    <v-dialog v-model="showSuccessDialog" max-width="500" persistent>
      <v-card class="success-dialog-card">
        <v-card-title class="success-dialog-header">
          <div class="success-icon-wrapper">
            <v-icon color="white" size="48">mdi-check-circle</v-icon>
          </div>
        </v-card-title>

        <v-card-text class="success-dialog-content">
          <h2 class="success-title">Feedback Submitted Successfully!</h2>
          <p class="success-message">
            Thank you for your feedback. Your response has been recorded.
          </p>
        </v-card-text>

        <v-card-actions class="success-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            large
            class="success-dialog-btn"
            @click="closeSuccessDialog"
          >
            <v-icon left>mdi-check</v-icon>
            OK
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ReportServices from '@/services/ReportServices'
import VIV from '@/events/VIV.vue'

export default {
  ProgramId: "",
  name: 'app',
  components: {
    VIV
  },
  data: function(){
    return {
      activeTab: 0,
      headers: [
          {text: 'Program Name', align: 'left', value: 'programName', flex: 1,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Coordinator', align: 'left', value: 'coordinatorName', flex: 1, class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Coordinator Email', align: 'left', value: 'coordinatorEmail',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Program Code', align: 'left', value: 'programId',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Status', align: 'left', value: 'status',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Zone', align: 'left', value: 'zoneDetails',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Zone-Timing', align: 'left', value: 'timeSlot',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          //{text: 'CheckInBy', align: 'left', value: 'checkInBy',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Registration', align: 'left', value: 'registration',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Category', align: 'left', value: 'Category',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
      ],
      pheaders: [
          {text: 'Participant Name', align: 'left', value: 'Participant',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Membership Email', align: 'left', value: 'EmailAddress',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Program Name', align: 'left', value: 'ProgramName', flex: 1,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Program Code', align: 'left', value: 'ProgramCode',flex: 1, sortable: true,  class: "font-weight-black indigo lighten-5 black--text", divider: true },
          {text: 'Coordinator Name', align: 'left', value: 'Coordinator', flex: 1, class: "font-weight-black indigo lighten-5 black--text", divider: true },
      ],
      profileData: [],
      programChanges: [],
      programMatch: undefined,
      pData: [],
      title: "Participant List",
      theme: 'default',
      defaults: this.getDefaults(),
      columns: this.getColumns(),
      loading: false,
      panel: [0, 0],
      nameRules: [(v) => !!v || "This Field is required"],
      form: false,
      valid: true,
      text: "",
      display: false,
      noData: false,
      paging: true,
      infoCorrect: false, // Toggle state
      feedbackType: null, // 'looksGood' or 'needsUpdate'
      feedbackComments: '',
      showSuccessDialog: false,
      Json: {
        Comments: "",
        ProgramId: this.ProgramId,
        Changes: "Yes"
      },
      bJson: {
        Comments: "",
        ProgramId: this.ProgramId,
        Changes: "No"
      }
    };
  },
  methods: {
    async clear() {
      this.ProgramId = ''
      this.profileData= []
      this.pData= []
      this.display = false
      this.noData = false
      this.infoCorrect = false
      this.feedbackType = null
      this.feedbackComments = ''
    },

    async submitFeedback() {
      // Get the first program ID from profileData
      const programId = this.profileData && this.profileData.length > 0
        ? this.profileData[0].programId
        : this.ProgramId

      // Create the feedback object
      const feedbackData = {
        ProgramId: programId,
        Comments: this.feedbackType === 'needsUpdate' ? this.feedbackComments : '',
        Changes: this.feedbackType === 'needsUpdate' ? 'Yes' : 'No'
      }

      try {
        // Post to programChanges endpoint
        await ReportServices.programChanges(feedbackData)

        // Show success dialog
        this.showSuccessDialog = true
      } catch (error) {
        // Error handling can be added here if needed
      }
    },
    closeSuccessDialog() {
      // Close dialog and reset the page
      this.showSuccessDialog = false
      this.clear()
    },
    async validateProgramId() {
      this.Json.ProgramId = this.ProgramId
      this.bJson.ProgramId = this.ProgramId
      this.refresh();
      setTimeout(() => {
        if (this.profileData === null || this.profileData == ""){
          this.noData = true
        } else  {
          this.display = true
          this.noData = false
        }
      }, 800)
    },
    async refresh() {
      const rawData = (await ReportServices.validateProgramId(this.ProgramId)).data
      // Transform data to camelCase properties
      this.profileData = rawData ? rawData.map(item => ({
        programName: item['Program Name'],
        coordinatorName: item['Coordinator Name'],
        coordinatorEmail: item['Coordinator Email'],
        programId: item['ProgramId'],
        status: item['Status'],
        zoneDetails: item['ZoneDetails'],
        timeSlot: item['TimeSlot'],
        checkInBy: item['CheckInBy'],
        registration: item['Registration'],
        programMatch: item.programMatch,
        Category: item.Category
      })) : []
      this.pData = (await ReportServices.getParticipantsByEmail(this.ProgramId)).data
      this.programChanges = (await ReportServices.getProgramChanges()).data;
    },
    async checkProgramId(item) {
      item.programMatch = this.programChanges.some(change => change.ProgramId === item.programId) ? "Yes" : undefined;
    },
    getColor (status) {
      if (status === 'Submitted Successfully') return 'green'
      else return 'orange'
    },
    getZoneColor (status) {
      if (status === 'ZONE 1') return 'brown'
      else if (status === 'ZONE 2') return 'green'
      else if (status === 'ZONE 3') return 'blue'
      else return 'orange'
    },
    async Update() {
      ReportServices.programChanges(this.Json)
      setTimeout(() => {
        this.$router.push({ path: "/ThankYou", replace: true });
      }, 500)
    },
    async good() {
      ReportServices.programChanges(this.bJson)
      setTimeout(() => {
        this.$router.push({ path: "/ThankYou", replace: true });
      }, 500)
    },
    getColumns: function(){
      return [
          {index: 'Zone', align: 'left', title: 'Zone'},
          {index: 'Coordinator Name', align: 'left', title: 'Coordinator Name', flex: 1 },
          {index: 'Program Name', align: 'left', title: 'Program Name', flex: 1},
          {index: 'Participant Name', align: 'left', title: 'Participant Name',flex: 1, sortable: true}
          ]
      },
    getDefaults: function(){
      return {
        type: 'string',
        width: 100,
        sortable: true,
        editable: true,
        resizable: true,
        cellAlign: 'left'
      }
    }
  },
  watch: {
    feedbackType(newValue) {
      // Clear comments when switching to "Looks Good"
      if (newValue === 'looksGood') {
        this.feedbackComments = ''
      }
    }
  }
}
</script>


<style lang="scss" scoped>
/* ========================================
   VERIFY PROGRAM PAGE - MODERN STYLING
   ======================================== */

.verify-program-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
}

/* Hero Banner */
.hero-banner {
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 20px;
  text-align: center;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="rgba(255,255,255,0.1)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,138.7C960,139,1056,117,1152,101.3C1248,85,1344,75,1392,69.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>') no-repeat bottom;
  background-size: cover;
  opacity: 0.3;
}

.banner-content {
  position: relative;
  z-index: 1;
}

.banner-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: iconFloat 3s ease-in-out infinite;
}

@keyframes iconFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.banner-title {
  font-size: 36px;
  font-weight: 800;
  color: white;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.banner-subtitle {
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.6;
}

/* Modern Container */
.modern-container {
  padding: 40px 20px;
  max-width: 1400px;
}

/* Tabs Card */
.tabs-card {
  border-radius: 20px !important;
  overflow: hidden;
  background: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  margin-bottom: 0 !important;
}

.modern-tabs {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

.modern-tabs ::v-deep .v-tabs-bar {
  background: transparent !important;
}

.modern-tabs ::v-deep .v-tab {
  font-weight: 600 !important;
  font-size: 15px !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  color: #57738f !important;
  transition: all 0.3s ease !important;
  padding: 20px 24px !important;
}

.modern-tabs ::v-deep .v-tab:hover {
  background: rgba(102, 126, 234, 0.08) !important;
  color: #667eea !important;
}

.modern-tabs ::v-deep .v-tab--active {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.12) 100%) !important;
  color: #667eea !important;
  font-weight: 700 !important;
}

.modern-tabs ::v-deep .v-tabs-slider {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  height: 4px !important;
  border-radius: 4px 4px 0 0 !important;
}

/* Search Card */
.search-card {
  border-radius: 20px !important;
  overflow: hidden;
  background: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease;
}

.search-card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.search-card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 24px;
  color: white;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
}

.search-card-content {
  padding: 32px 24px !important;
}

.modern-search-field {
  margin-bottom: 20px;
}

.modern-search-field ::v-deep .v-input__control {
  border-radius: 12px;
}

.modern-search-field ::v-deep .v-text-field__details {
  padding-top: 8px;
}

.search-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-start;
}

.modern-validate-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  border-radius: 12px !important;
  padding: 10px 28px !important;
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease !important;
}

.modern-validate-btn:hover {
  background: linear-gradient(135deg, #45a049 0%, #4CAF50 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4) !important;
}

.modern-clear-btn {
  color: #667eea !important;
  border-color: #667eea !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  border-radius: 12px !important;
  padding: 10px 28px !important;
  transition: all 0.3s ease !important;
}

.modern-clear-btn:hover {
  background: rgba(102, 126, 234, 0.1) !important;
  transform: translateY(-2px);
}

/* Modern Alert */
.modern-alert {
  border-radius: 16px !important;
  padding: 20px !important;
}

.alert-content {
  display: flex;
  align-items: center;
}

.alert-title {
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
}

.alert-message {
  font-size: 14px;
  font-weight: 500;
  color: #57738f;
  line-height: 1.5;
}

/* Important Note Card - Compact */
.important-note-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #2196F3;
}

.note-card-header {
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  padding: 12px 18px;
  color: white;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  letter-spacing: 0.3px;
}

.note-card-content {
  padding: 16px 18px !important;
}

.note-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.note-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: #2c3e50;
}

.note-item:last-child {
  margin-bottom: 0;
  color: #d32f2f;
}

.note-item span {
  flex: 1;
}

.note-item strong {
  color: #667eea;
  font-weight: 700;
}

.note-item:last-child strong {
  color: #d32f2f;
}

.note-item ::v-deep .v-icon {
  margin-top: 2px;
}

/* Data Table Card */
.data-table-card {
  border-radius: 20px !important;
  overflow: hidden;
  background: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

/* Feedback Card */
.feedback-card {
  border-radius: 20px !important;
  overflow: hidden;
  background: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  border: 2px solid #f5f5f5;
}

.feedback-card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 24px;
  color: white;
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.feedback-card-content {
  padding: 24px;
}

.feedback-options {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.feedback-options ::v-deep .v-input--radio-group__input {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.feedback-radio {
  margin-top: 0 !important;
  padding-top: 0 !important;
  margin-bottom: 0 !important;
}

.feedback-radio ::v-deep .v-label {
  font-size: 16px;
  font-weight: 600;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.comment-section {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.feedback-textarea ::v-deep .v-input__control {
  border-radius: 12px;
}

.feedback-textarea ::v-deep textarea {
  font-size: 14px;
  line-height: 1.6;
}

.feedback-actions {
  display: flex;
  justify-content: flex-end;
}

.submit-feedback-btn {
  border-radius: 12px !important;
  padding: 12px 32px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;
}

.submit-feedback-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4) !important;
}

.submit-feedback-btn:disabled {
  opacity: 0.5;
  transform: none !important;
}

/* Success Dialog */
.success-dialog-card {
  border-radius: 20px !important;
  overflow: hidden;
  text-align: center;
}

.success-dialog-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  padding: 32px 24px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: successPulse 0.6s ease-out;
}

@keyframes successPulse {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-dialog-content {
  padding: 32px 24px !important;
}

.success-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
}

.success-message {
  font-size: 16px;
  color: #5a6c7d;
  line-height: 1.6;
  margin: 0;
}

.success-dialog-actions {
  padding: 16px 24px 24px !important;
}

.success-dialog-btn {
  border-radius: 12px !important;
  padding: 12px 40px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;
}

.success-dialog-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4) !important;
}

/* Mobile Scroll Hint */
.mobile-scroll-hint {
  display: none;
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  padding: 10px 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #e65100;
  border-bottom: 2px solid #ffb74d;
}

.mobile-scroll-hint v-icon {
  color: #e65100;
}

.table-card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 24px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.header-chip {
  background: rgba(255, 255, 255, 0.25) !important;
  backdrop-filter: blur(10px);
  color: white !important;
  font-weight: 600 !important;
}

/* Modern Data Table */
.modern-data-table {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.modern-data-table ::v-deep thead {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modern-data-table ::v-deep thead th {
  background: transparent !important;
  color: #2c3e50 !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  border-bottom: 2px solid #667eea !important;
  padding: 16px !important;
}

.modern-data-table ::v-deep tbody tr {
  transition: all 0.2s ease;
}

.modern-data-table ::v-deep tbody tr:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%) !important;
  transform: scale(1.01);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.modern-data-table ::v-deep tbody td {
  padding: 16px !important;
  font-size: 14px !important;
  color: #2c3e50 !important;
  border-bottom: 1px solid #e9ecef !important;
}

/* Table Cell Styles */
.program-name-cell {
  display: flex;
  align-items: center;
}

.program-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.coordinator-cell {
  display: flex;
  align-items: center;
}

.email-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #57738f;
}

.program-code-chip {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
  color: #1976d2 !important;
  font-weight: 600 !important;
  font-family: 'Courier New', monospace !important;
}

.status-chip {
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
}

.zone-chip {
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
}

/* Loading Wrapper */
.loading-wrapper {
  text-align: center;
  padding: 60px 20px;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #667eea;
}

/* Responsive Design */
@media (max-width: 960px) {
  .hero-banner {
    padding: 35px 20px;
  }

  .banner-title {
    font-size: 30px;
  }

  .banner-subtitle {
    font-size: 14px;
    flex-direction: column;
  }

  .banner-icon-wrapper {
    width: 70px;
    height: 70px;
    margin-bottom: 14px;
  }

  .modern-container {
    padding: 24px 12px;
  }

  .tabs-card {
    border-radius: 16px !important;
  }

  .modern-tabs ::v-deep .v-tab {
    font-size: 13px !important;
    padding: 16px 12px !important;
  }

  .search-card-content {
    padding: 24px 16px !important;
  }

  .search-actions {
    flex-direction: column;
  }

  .modern-validate-btn,
  .modern-clear-btn {
    width: 100%;
  }

  .important-note-card {
    border-radius: 14px !important;
  }

  .note-card-header {
    font-size: 14px !important;
    padding: 10px 16px !important;
  }

  .note-card-content {
    padding: 14px !important;
  }

  .note-item {
    font-size: 12px;
    margin-bottom: 8px;
  }

  .table-card-header,
  .feedback-card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    font-size: 16px !important;
    padding: 16px 18px !important;
  }

  .feedback-card {
    border-radius: 16px !important;
  }

  .feedback-card-content {
    padding: 18px !important;
  }

  .feedback-options {
    flex-direction: column;
    gap: 16px;
  }

  .checkbox-label {
    font-size: 15px;
  }

  .feedback-textarea ::v-deep textarea {
    font-size: 13px;
  }

  .submit-feedback-btn {
    width: 100%;
    font-size: 15px !important;
  }

  /* Success Dialog Responsive */
  .success-dialog-card {
    border-radius: 16px !important;
  }

  .success-icon-wrapper {
    width: 70px;
    height: 70px;
  }

  .success-icon-wrapper .v-icon {
    font-size: 42px !important;
  }

  .success-title {
    font-size: 22px;
  }

  .success-message {
    font-size: 15px;
  }

  .success-dialog-btn {
    font-size: 15px !important;
    padding: 10px 32px !important;
  }

  .modern-data-table ::v-deep tbody td {
    font-size: 12px !important;
    padding: 12px 8px !important;
  }

  .modern-data-table ::v-deep thead th {
    font-size: 11px !important;
    padding: 12px 8px !important;
  }

  /* Make table scrollable on tablet */
  .data-table-card {
    overflow-x: auto;
  }

  .program-name-cell,
  .coordinator-cell,
  .email-cell {
    font-size: 12px;
  }

  .program-name-cell v-icon,
  .coordinator-cell v-icon,
  .email-cell v-icon {
    font-size: 16px !important;
  }
}

@media (max-width: 600px) {
  .verify-program-page {
    overflow-x: hidden;
  }

  .hero-banner {
    padding: 30px 16px;
  }

  .banner-title {
    font-size: 24px;
    line-height: 1.2;
  }

  .banner-subtitle {
    font-size: 13px;
    flex-direction: column;
    gap: 4px;
  }

  .banner-icon-wrapper {
    width: 60px;
    height: 60px;
    margin-bottom: 12px;
  }

  .banner-icon-wrapper v-icon {
    font-size: 36px !important;
  }

  .modern-container {
    padding: 16px 8px !important;
  }

  .tabs-card {
    border-radius: 14px !important;
    margin: 0 4px;
  }

  .modern-tabs ::v-deep .v-tab {
    font-size: 11px !important;
    padding: 14px 8px !important;
    min-width: 120px !important;
  }

  .modern-tabs ::v-deep .v-tab .v-icon {
    font-size: 18px !important;
    margin-right: 4px !important;
  }

  .mt-4 {
    margin-top: 12px !important;
  }

  .search-card,
  .data-table-card,
  .important-note-card {
    border-radius: 14px !important;
  }

  .search-card,
  .important-note-card {
    border-radius: 14px !important;
  }

  .data-table-card {
    border-radius: 14px !important;
  }

  .search-card-header,
  .table-card-header,
  .note-card-header,
  .feedback-card-header {
    padding: 14px 16px !important;
    font-size: 15px !important;
  }

  .feedback-card {
    border-radius: 14px !important;
  }

  .feedback-card-content {
    padding: 16px !important;
  }

  .checkbox-label {
    font-size: 14px;
  }

  .feedback-textarea ::v-deep textarea {
    font-size: 12px;
  }

  .submit-feedback-btn {
    font-size: 14px !important;
    padding: 10px 24px !important;
  }

  /* Success Dialog Responsive */
  .success-dialog-card {
    border-radius: 14px !important;
    margin: 0 8px;
  }

  .success-dialog-header {
    padding: 24px 16px !important;
  }

  .success-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .success-icon-wrapper .v-icon {
    font-size: 36px !important;
  }

  .success-dialog-content {
    padding: 24px 16px !important;
  }

  .success-title {
    font-size: 20px;
  }

  .success-message {
    font-size: 14px;
  }

  .success-dialog-actions {
    padding: 12px 16px 20px !important;
  }

  .success-dialog-btn {
    font-size: 14px !important;
    padding: 10px 28px !important;
  }

  .note-card-content {
    padding: 12px !important;
  }

  .note-item {
    font-size: 11px;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  .note-item ::v-deep .v-icon {
    font-size: 14px !important;
  }

  .search-card-content {
    padding: 20px 16px !important;
  }

  .modern-validate-btn,
  .modern-clear-btn {
    padding: 12px 20px !important;
    font-size: 13px !important;
  }

  .alert-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .alert-title {
    font-size: 16px;
  }

  .alert-message {
    font-size: 13px;
  }

  /* Compact table - no horizontal scroll */
  .data-table-card {
    overflow-x: visible;
    margin: 0 4px !important;
  }

  .data-table-card ::v-deep .v-card__text {
    padding: 0 !important;
  }

  .modern-data-table ::v-deep table {
    width: 100%;
    table-layout: auto;
  }

  .modern-data-table ::v-deep tbody td {
    font-size: 11px !important;
    padding: 8px 4px !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    line-height: 1.4 !important;
  }

  .modern-data-table ::v-deep thead th {
    font-size: 10px !important;
    padding: 8px 4px !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    line-height: 1.3 !important;
  }

  .program-name-cell,
  .coordinator-cell,
  .email-cell {
    font-size: 11px;
    flex-wrap: wrap;
    gap: 3px;
  }

  .program-name {
    font-size: 11px;
    word-break: break-word;
    line-height: 1.3;
  }

  .program-code-chip,
  .status-chip,
  .zone-chip {
    font-size: 9px !important;
    height: 20px !important;
    padding: 0 6px !important;
  }

  .program-code-chip ::v-deep .v-icon,
  .status-chip ::v-deep .v-icon,
  .zone-chip ::v-deep .v-icon {
    font-size: 11px !important;
  }

  .coordinator-cell ::v-deep .v-avatar {
    width: 24px !important;
    height: 24px !important;
  }

  .coordinator-cell ::v-deep .v-avatar .v-icon {
    font-size: 13px !important;
  }

  .loading-wrapper {
    padding: 40px 16px;
  }

  .loading-text {
    font-size: 14px;
  }
}

/* Extra small devices */
@media (max-width: 400px) {
  .banner-title {
    font-size: 22px;
  }

  .banner-subtitle {
    font-size: 12px;
  }

  .banner-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .modern-container {
    padding: 12px 4px !important;
  }

  .tabs-card {
    margin: 0 2px;
    border-radius: 12px !important;
  }

  .search-card,
  .important-note-card,
  .data-table-card,
  .feedback-card {
    border-radius: 12px !important;
  }

  .modern-tabs ::v-deep .v-tab {
    font-size: 10px !important;
    padding: 12px 6px !important;
    min-width: 100px !important;
  }

  .modern-tabs ::v-deep .v-tab .v-icon {
    font-size: 16px !important;
    margin-right: 2px !important;
  }

  .search-card-header,
  .table-card-header,
  .note-card-header,
  .feedback-card-header {
    font-size: 13px !important;
    padding: 12px 14px !important;
  }

  .note-card-content {
    padding: 10px !important;
  }

  .note-item {
    font-size: 10px;
    margin-bottom: 6px;
    line-height: 1.3;
  }

  .note-item ::v-deep .v-icon {
    font-size: 12px !important;
    margin-right: 3px !important;
  }

  .feedback-card-content {
    padding: 12px !important;
  }

  .feedback-options {
    gap: 12px;
  }

  .checkbox-label {
    font-size: 12px;
  }

  .checkbox-label .v-icon {
    font-size: 16px !important;
  }

  .feedback-textarea ::v-deep textarea {
    font-size: 11px;
  }

  .submit-feedback-btn {
    font-size: 12px !important;
    padding: 8px 16px !important;
  }

  /* Success Dialog Responsive */
  .success-dialog-card {
    border-radius: 12px !important;
    margin: 0 4px;
  }

  .success-dialog-header {
    padding: 20px 12px !important;
  }

  .success-icon-wrapper {
    width: 50px;
    height: 50px;
  }

  .success-icon-wrapper .v-icon {
    font-size: 30px !important;
  }

  .success-dialog-content {
    padding: 20px 12px !important;
  }

  .success-title {
    font-size: 18px;
  }

  .success-message {
    font-size: 13px;
  }

  .success-dialog-actions {
    padding: 10px 12px 16px !important;
  }

  .success-dialog-btn {
    font-size: 13px !important;
    padding: 8px 20px !important;
  }

  .data-table-card {
    margin: 0 2px !important;
  }

  /* Even more compact for very small screens */
  .modern-data-table ::v-deep tbody td {
    font-size: 10px !important;
    padding: 6px 3px !important;
  }

  .modern-data-table ::v-deep thead th {
    font-size: 9px !important;
    padding: 6px 3px !important;
  }

  .program-name-cell,
  .coordinator-cell,
  .email-cell {
    font-size: 10px;
    gap: 2px;
  }

  .program-name {
    font-size: 10px;
  }

  .program-code-chip,
  .status-chip,
  .zone-chip {
    font-size: 8px !important;
    height: 18px !important;
    padding: 0 4px !important;
  }

  .program-code-chip ::v-deep .v-icon,
  .status-chip ::v-deep .v-icon,
  .zone-chip ::v-deep .v-icon {
    font-size: 10px !important;
  }

  .coordinator-cell ::v-deep .v-avatar {
    width: 20px !important;
    height: 20px !important;
  }

  .coordinator-cell ::v-deep .v-avatar .v-icon {
    font-size: 11px !important;
  }
}
</style>
